import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../interceptors";

// ── useInterviewSession (prompt #3) ────────────────────────────────────────
// Wraps the InterviewSessionController endpoints. Use during a candidate
// recording flow to track current question + chunk uploads + last
// heartbeat. If the tab closes, the row stays IN_PROGRESS on the server
// and ResumeSessionModal will offer Continue/Restart on next mount.
//
// Heartbeat cadence: every 10s while the session is active. Matches the
// spec's "post a heartbeat with current state every 10 seconds".
//
// Exposes:
//   session         object   the current session row (null until create)
//   create({interviewId, candidateId, currentQuestionIdx?, clientState?})
//   patch({currentQuestionIdx?, status?, clientState?, networkQualityLog?, questionId?})
//   registerChunk({chunkId, s3Key, sizeBytes, uploadedAt})
//   stitch()        finalize → status=UPLOADING
//   abort()         clears state without server changes (browser-close
//                   path; server-side staleness scheduler handles cleanup)
const BASE = "/job-posting/interview-sessions";
const HEARTBEAT_MS = 10_000;

export default function useInterviewSession() {
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const sessionRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  sessionRef.current = session;

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) return;
    heartbeatTimerRef.current = setInterval(async () => {
      const sid = sessionRef.current?.id;
      if (!sid) return;
      try {
        // Empty PATCH body — server bumps last_heartbeat_at to NOW.
        await axiosInstance.patch(`${BASE}/${sid}`, {});
      } catch (e) {
        // Silent — next heartbeat will retry. Don't surface intermittent
        // network errors as UI noise.
      }
    }, HEARTBEAT_MS);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const create = useCallback(
    async ({ interviewId, candidateId, currentQuestionIdx, clientState }) => {
      setError(null);
      try {
        const { data } = await axiosInstance.post(BASE, {
          interviewId,
          candidateId,
          currentQuestionIdx: currentQuestionIdx ?? 0,
          clientState: clientState ? JSON.stringify(clientState) : undefined,
        });
        setSession(data);
        startHeartbeat();
        return data;
      } catch (e) {
        setError(e?.message || "Couldn't start session");
        throw e;
      }
    },
    [startHeartbeat],
  );

  const patch = useCallback(async (changes) => {
    const sid = sessionRef.current?.id;
    if (!sid) return null;
    const body = { ...changes };
    if (changes.clientState && typeof changes.clientState !== "string") {
      body.clientState = JSON.stringify(changes.clientState);
    }
    if (changes.networkQualityLog && typeof changes.networkQualityLog !== "string") {
      body.networkQualityLog = JSON.stringify(changes.networkQualityLog);
    }
    try {
      const { data } = await axiosInstance.patch(`${BASE}/${sid}`, body);
      setSession((s) => ({ ...(s || {}), ...data }));
      return data;
    } catch (e) {
      setError(e?.message || "Couldn't update session");
      throw e;
    }
  }, []);

  const registerChunk = useCallback(async (chunk) => {
    const sid = sessionRef.current?.id;
    if (!sid) return null;
    try {
      const { data } = await axiosInstance.post(`${BASE}/${sid}/chunk`, chunk);
      return data; // { id, chunkCount }
    } catch (e) {
      setError(e?.message || "Chunk upload register failed");
      throw e;
    }
  }, []);

  const stitch = useCallback(async () => {
    const sid = sessionRef.current?.id;
    if (!sid) return null;
    stopHeartbeat();
    try {
      const { data } = await axiosInstance.post(`${BASE}/${sid}/stitch`);
      setSession((s) => ({ ...(s || {}), status: "UPLOADING", ...data }));
      return data;
    } catch (e) {
      setError(e?.message || "Stitch failed");
      throw e;
    }
  }, [stopHeartbeat]);

  const abort = useCallback(() => {
    stopHeartbeat();
    setSession(null);
  }, [stopHeartbeat]);

  // Adopt an existing session that came from outside (e.g. resume flow
  // selected a found session).
  const adopt = useCallback((existing) => {
    setSession(existing);
    startHeartbeat();
  }, [startHeartbeat]);

  // Cleanup on unmount.
  useEffect(() => stopHeartbeat, [stopHeartbeat]);

  return { session, error, create, patch, registerChunk, stitch, abort, adopt };
}

/**
 * Lookup helper. Calls GET /by-candidate/{id} once. Returns the resumable
 * session row or null.
 */
export async function findResumableSession(candidateId) {
  if (!candidateId) return null;
  try {
    const { data } = await axiosInstance.get(`${BASE}/by-candidate/${candidateId}`);
    return data?.session || null;
  } catch {
    return null;
  }
}
